import { UserProgress } from '../types';

export class SpacedRepetition {
  // SM-2 Algorithm implementation
  static calculateNextReview(progress: UserProgress, quality: number): UserProgress {
    // Quality should be between 0-5
    const clampedQuality = Math.max(0, Math.min(5, quality));

    let { easeFactor, interval, repetitions } = progress;

    // Update ease factor
    easeFactor = Math.max(1.3, easeFactor + (0.1 - (5 - clampedQuality) * (0.08 + (5 - clampedQuality) * 0.02)));

    if (clampedQuality < 3) {
      // Failed recall - reset repetitions
      repetitions = 0;
    } else {
      // Successful recall - increment repetitions
      repetitions += 1;
    }

    // Calculate new interval
    if (repetitions === 0) {
      interval = 1; // First repetition
    } else if (repetitions === 1) {
      interval = 1; // Second repetition
    } else if (repetitions === 2) {
      interval = 6; // Third repetition
    } else {
      interval = Math.round(interval * easeFactor);
    }

    // Cap interval at 6 months (180 days) to prevent extremely long intervals
    interval = Math.min(interval, 180);

    // Calculate next review date
    const nextReviewDate = new Date();
    nextReviewDate.setDate(nextReviewDate.getDate() + interval);

    return {
      ...progress,
      easeFactor,
      interval,
      repetitions,
      nextReviewDate,
      lastReviewed: new Date(),
      quality: clampedQuality,
      timesCorrect: clampedQuality >= 3 ? progress.timesCorrect + 1 : progress.timesCorrect,
      timesIncorrect: clampedQuality < 3 ? progress.timesIncorrect + 1 : progress.timesIncorrect
    };
  }

  // Get cards due for review
  static getDueCards(progressRecords: UserProgress[]): UserProgress[] {
    const now = new Date();
    return progressRecords.filter(record => record.nextReviewDate <= now);
  }

  // Calculate optimal study order based on priority
  static sortByPriority(progressRecords: UserProgress[]): UserProgress[] {
    const now = new Date();

    return progressRecords.sort((a, b) => {
      // Priority based on:
      // 1. Overdue cards (negative days)
      // 2. Cards due soon (smaller positive interval)
      // 3. Lower ease factor (harder cards)

      const daysOverdueA = Math.floor((now.getTime() - a.nextReviewDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysOverdueB = Math.floor((now.getTime() - b.nextReviewDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysOverdueA !== daysOverdueB) {
        return daysOverdueB - daysOverdueA; // More overdue first
      }

      if (a.nextReviewDate.getTime() !== b.nextReviewDate.getTime()) {
        return a.nextReviewDate.getTime() - b.nextReviewDate.getTime(); // Sooner due date first
      }

      return a.easeFactor - b.easeFactor; // Lower ease factor (harder cards) first
    });
  }

  // Calculate retention rate for a user
  static calculateRetentionRate(progressRecords: UserProgress[]): number {
    const totalAttempts = progressRecords.reduce((sum, record) =>
      sum + record.timesCorrect + record.timesIncorrect, 0);

    if (totalAttempts === 0) return 0;

    const totalCorrect = progressRecords.reduce((sum, record) => sum + record.timesCorrect, 0);
    return (totalCorrect / totalAttempts) * 100;
  }

  // Get study recommendations
  static getStudyRecommendations(progressRecords: UserProgress[]): {
    newCards: number;
    reviewCards: number;
    difficultCards: number;
  } {
    const dueCards = this.getDueCards(progressRecords);
    const difficultCards = progressRecords.filter(record =>
      record.easeFactor < 1.8 && record.repetitions > 2
    );

    return {
      newCards: Math.max(0, 20 - dueCards.length), // Max 20 new cards per day
      reviewCards: dueCards.length,
      difficultCards: difficultCards.length
    };
  }
}
